import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CREDIT_API_URL } from '../../constants.js';
import OtpVerificationModal from '../../utility/OtpVerificationModal';
import getClientInfo from '../../utility/getClientInfo';
import useChatStore from '../../store/useChatStore';
import useUtmStore from '../../store/useUtmStore';
import { fadeInLeft, fadeInRight, fadeInUp, buttonVariants } from '../../utility/framerMotionAnimations';
import './BusinessExpansionHeroSection.css';

const BusinessExpansionHeroSection = forwardRef(({ onScrollToForm }, ref) => {
  const { t } = useTranslation('businessExpansionPage');
  const setCustomer = useChatStore((state) => state.setCustomer);
  const utm = useUtmStore(state => state.utm || {});
  const [loanFactorApiResponse, setLoanFactorApiResponse] = useState({
    minLoanAmount: 2500,
    maxLoanAmount: 300000,
  });
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [mobileNumber, setMobileNumber] = useState('');
  const [consent, setConsent] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [loanAmountText, setLoanAmountText] = useState('10,00,000');
  const [isEditingLoanAmount, setIsEditingLoanAmount] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    mobileNumber: false,
    consent: false
  });
  const [loanAmountError, setLoanAmountError] = useState('');
  const [mobileNumberError, setMobileNumberError] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const debounceTimeout = useRef(null);
  const { browserName, deviceType, userAgent } = getClientInfo();
  // Detect mobile screen
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Fetching Loan Factor
  const fetchLoanFactor = useCallback(async () => {
    try {
      const payload = { productType: 'Business Loan' }; // Business loan for business expansion
      const response = await axios.post(`${CREDIT_API_URL}fetch-loan-factor`, payload);
      const data = response.data.data;
      setLoanFactorApiResponse(data);
      // Initialize to 10,00,000 on page load, clamped to API range
      const desiredDefault = 1000000;
      const clampedDefault = Math.min(
        Math.max(desiredDefault, data.minLoanAmount),
        data.maxLoanAmount
      );
      setLoanAmount(clampedDefault);
      setLoanAmountText(formatCurrency(clampedDefault));
    } catch (error) {
      console.error("Error fetching loan factor:", error.response ? error.response.data : error.message);
      // Fallback to default values if API fails
      setLoanFactorApiResponse({
        minLoanAmount: 5000,
        maxLoanAmount: 300000,
      });
      setLoanAmount(1000000);
      setLoanAmountText(formatCurrency(1000000));
    }
  }, []);

  useEffect(() => {
    fetchLoanFactor();
  }, [fetchLoanFactor]);

  const handleSliderChange = (e) => {
    const newAmount = parseInt(e.target.value);
    setLoanAmount(newAmount);
    setLoanAmountText(formatCurrency(newAmount));
    // Clear loan amount error when slider is moved to valid range
    setLoanAmountError('');
    // Trigger animation by updating the key
    setAnimationKey(prev => prev + 1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const validateMobileNumber = (mobile) => {
    // Validate 10-digit Indian mobile number
    return /^[6-9]\d{9}$/.test(mobile);
  };

  const handleLoanAmountInput = (e) => {
    setIsEditingLoanAmount(true);
    const value = e.target.innerText.trim().replace(/[,]/g, '').trim();
    const parsedValue = parseInt(value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (!isNaN(parsedValue)) {
        if (parsedValue >= loanFactorApiResponse.minLoanAmount && parsedValue <= loanFactorApiResponse.maxLoanAmount) {
          setLoanAmount(parsedValue);
          setLoanAmountText(formatCurrency(parsedValue));
          setAnimationKey(prev => prev + 1);
          setLoanAmountError(''); // Clear error
          
          // Move cursor to end after update
          setTimeout(() => {
            const element = e.target;
            if (element) {
              const range = document.createRange();
              const selection = window.getSelection();
              range.selectNodeContents(element);
              range.collapse(false); // false means collapse to end
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }, 0);
        } else {
          // Show error message for out of range values
          if (parsedValue < loanFactorApiResponse.minLoanAmount) {
            setLoanAmountError(t('business-expansion-hero-section-loan-amount-error', { 
              min: formatCurrency(loanFactorApiResponse.minLoanAmount), 
              max: formatCurrency(loanFactorApiResponse.maxLoanAmount) 
            }));
          } else if (parsedValue > loanFactorApiResponse.maxLoanAmount) {
            setLoanAmountError(t('business-expansion-hero-section-loan-amount-error', { 
              min: formatCurrency(loanFactorApiResponse.minLoanAmount), 
              max: formatCurrency(loanFactorApiResponse.maxLoanAmount) 
            }));
          }
          
          // Move cursor to end after showing error
          setTimeout(() => {
            const element = e.target;
            if (element) {
              const range = document.createRange();
              const selection = window.getSelection();
              range.selectNodeContents(element);
              range.collapse(false); // false means collapse to end
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }, 0);
        }
      } else {
        setLoanAmountText(formatCurrency(loanAmount));
        setLoanAmountError(t('business-expansion-hero-section-valid-amount-error'));
        
        // Move cursor to end after reverting
        setTimeout(() => {
          const element = e.target;
          if (element) {
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(element);
            range.collapse(false); // false means collapse to end
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 0);
      }
    }, 300);
  };

  const handleStartApplication = async () => {
    const errors = {
      mobileNumber: !mobileNumber || !validateMobileNumber(mobileNumber),
      consent: !consent
    };

    setValidationErrors(errors);

    // If there are no errors, proceed with lead creation
    if (!errors.mobileNumber && !errors.consent) {
      setIsProcessing(true);
      try {
        // Create lead
        const leadPayload = {
          name: '', // Optional for homepage
          mobile: parseInt(mobileNumber),
          email: '', // Optional for homepage
          productType: 'Business Loan',
          loanAmount: loanAmount,
          businessName: '', // Optional for homepage
          isConsentAccepted: consent
        };

         // Attach client/browser info
         try {
          const clientInfo = getClientInfo();
          leadPayload.browserName = clientInfo.browserName || null;
          leadPayload.deviceType = clientInfo.deviceType || null;
    
        } catch (e) {
          // ignore detection errors
        }
         // Include UTM tracking params if present
         if (utm) {
          leadPayload.utmSource = utm.utmSource || null;
          leadPayload.utmMedium = utm.utmMedium || null;
          leadPayload.utmCampaign = utm.utmCampaign || "GrowBizh";
        }

        const leadResponse = await axios.post(`${CREDIT_API_URL}create-lead`, leadPayload);
        
        if (leadResponse.status === 200) {
          const leadDataFromResponse = leadResponse.data.data;
          setLeadData(leadDataFromResponse);
          
          // Generate OTP
          const otpResponse = await axios.post(`${CREDIT_API_URL}generate-otp`, {
            mobile: parseInt(mobileNumber),
            productType: 'Business Loan'
          });
          
          if (otpResponse.status === 200) {
            setShowOtpModal(true);
          } else {
            throw new Error('OTP generation failed');
          }
        } else {
          throw new Error(leadResponse.data.message || 'Lead creation failed');
        }
      } catch (error) {
        console.error('Error in lead creation:', error);
        
        // Handle specific error case: Mobile not verified for this product type
        if (error.response?.status === 400 && 
            error.response?.data?.message === "Mobile is not verified for this product type. Please verify your mobile number first.") {
          
          // Set lead data from the error response
          if (error.response?.data?.data) {
            const leadDataFromError = error.response.data.data;
            setLeadData(leadDataFromError);
          }
          
          // Generate OTP for verification
          try {
            const otpResponse = await axios.post(`${CREDIT_API_URL}generate-otp`, {
              mobile: parseInt(mobileNumber),
              productType: 'Business Loan'
            });
            
            if (otpResponse.status === 200) {
              setShowOtpModal(true);
            } else {
              throw new Error('OTP generation failed');
            }
          } catch (otpError) {
            console.error('Error generating OTP:', otpError);
            setErrorMessage('Failed to send OTP. Please try again.');
          }
        } else if (error.response?.status === 409) {
            // Handle existing lead case
            const leadDataFromError = error.response?.data?.data;
            
            if (leadDataFromError) {
                // Store lead data and proceed with OTP verification regardless of eligibility
                setLeadData(leadDataFromError);
                
                // Generate OTP for verification
                try {
                    const otpResponse = await axios.post(`${CREDIT_API_URL}generate-otp`, {
                        mobile: parseInt(mobileNumber),
                        productType: 'Business Loan'
                    });
                    
                    if (otpResponse.status === 200) {
                        setShowOtpModal(true);
                    } else {
                        throw new Error('OTP generation failed');
                    }
                } catch (otpError) {
                    console.error('Error generating OTP:', otpError);
                    setErrorMessage('Failed to send OTP. Please try again.');
                }
            } else {
                // No lead data in error response, show error
                setErrorMessage(error.response?.data?.message || 'Lead already exists but no data received.');
            }
        } else {
          // Handle other errors
          setErrorMessage(error.response?.data?.message || error.message || 'Something went wrong. Please try again.');
        }
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleMobileNumberChange = (e) => {
    let value = e.target.value;
    
    // Remove any non-digit characters
    value = value.replace(/[^\d]/g, '');
    
    // Limit to 10 characters (10 digits)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setMobileNumber(value);
    
    // Real-time validation
    if (value) {
      if (validateMobileNumber(value)) {
        setMobileNumberError('');
        setValidationErrors(prev => ({ ...prev, mobileNumber: false }));
      } else {
        setMobileNumberError(t('business-expansion-hero-section-mobile-error'));
        setValidationErrors(prev => ({ ...prev, mobileNumber: true }));
      }
    } else {
      setMobileNumberError('');
      setValidationErrors(prev => ({ ...prev, mobileNumber: false }));
    }
  };

  const handleConsentChange = (e) => {
    const checked = e.target.checked;
    setConsent(checked);
    // Clear validation error when user checks the box
    if (validationErrors.consent) {
      setValidationErrors(prev => ({ ...prev, consent: false }));
    }
  };

  const handleOtpSuccess = async () => {
    try {
      if (!leadData) throw new Error("Lead data missing. Please try again.");
      
      console.log("=== OTP Success Debug ===");
      console.log("Lead data:", leadData);
      console.log("Product type:", leadData.productType);
      console.log("Product type (lowercase):", leadData.productType?.toLowerCase());
      console.log("=========================");
      
      await setCustomer(leadData);
      useChatStore.setState({ chatSessionId: leadData.chatSessionId, loanId: leadData.loanRequestId });
      
      // ✅ Suppress Bizzie auto-redirect check for Business Expansion form
      // This ensures user always goes to Bizzie, never to dashboard
      useChatStore.getState().setSuppressAutoRedirect(10000); // 10 seconds suppression
      
      // More flexible product type checking
      const productType = leadData.productType?.toLowerCase() || '';
      console.log("Checking product type:", productType);
      
      if (productType.includes("commercial vehicle") || 
          productType.includes("business loan") || 
          productType === "business loan" ||
          productType === "commercial vehicle loan") {
        console.log("Product type matches, redirecting to bizzie...");
        window.location.href = `/bizzie`;
      } else {
        // Fallback: Since this is Business Expansion page, always redirect to bizzie
        console.log("Product type doesn't match, but this is Business Expansion page - redirecting to bizzie anyway");
        window.location.href = `/bizzie`;
      }
    } catch (err) {
      console.error("Error in handleOtpSuccess:", err);
      alert("Customer creation failed. Please try again.");
    }
  };

  // Check if all required fields are filled and values are within allowed ranges
  const isFormValid = () => {
    const isLoanAmountInRange =
      typeof loanAmount === 'number' &&
      loanAmount >= (loanFactorApiResponse?.minLoanAmount ?? 0) &&
      loanAmount <= (loanFactorApiResponse?.maxLoanAmount ?? Number.MAX_SAFE_INTEGER);

    const isMobileValid = mobileNumber && validateMobileNumber(mobileNumber);
    const hasNoErrors = !loanAmountError && !mobileNumberError;

    return isLoanAmountInRange && isMobileValid && consent && hasNoErrors;
  };

  return (
    <section ref={ref} className="business-hero-section">
      <div className="business-hero-wrapper">
        {/* Left side - Text Content */}
        <motion.div 
          className="business-hero-content"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
          variants={fadeInLeft}
        >
          <motion.h1 
            className="business-hero-heading"
            variants={fadeInUp}
          >
                    {t('business-expansion-hero-section-heading')}
          </motion.h1>
          <motion.p 
            className="business-hero-description"
            variants={fadeInUp}
            transition={{ delay: 0.2 }}
          >
            {t('business-expansion-hero-section-description')}
          </motion.p>
        </motion.div>

        {/* Right side - Form Card */}
        <motion.div 
          className="business-hero-card"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
          variants={fadeInRight}
        >
          {/* Title */}
          <h2 className="business-hero-title">
            <span className="business-hero-title-primary">{t('business-expansion-hero-section-title-primary')}</span>
            <span className="business-hero-title-highlight">{t('business-expansion-hero-section-title-highlight')}→</span>
          </h2>

          {/* Form Section */}
          <div className="business-form-section">
           

            <div className="business-form-section-bottom">
              <div className="business-form">
                <div className="business-form-content">
                  {/* Loan Amount Input */}
                  <div className={`business-input-field-container ${loanAmountError ? 'business-input-field-error' : ''}`}>
                    <span className="business-input-prefix">₹</span>
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onInput={handleLoanAmountInput}
                      onFocus={() => setIsEditingLoanAmount(true)}
                      onBlur={() => setIsEditingLoanAmount(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleLoanAmountInput(e);
                        }
                        // Prevent typing more than 10 characters
                        if (e.target.textContent && e.target.textContent.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      className="business-input-field business-content-editable-input"
                    >
                      {loanAmountText}
                    </div>
                    <div className="business-loan-amount-placeholder">{t('business-expansion-hero-section-loan-amount-placeholder')}</div>
                  </div>
                  {loanAmountError && (
                    <div id="business-frm-error" className="business-error-message">
                      {loanAmountError}
                    </div>
                  )}
                    {/* Slider */}
                <div className="business-slider-container">
                  <div className="business-slider-track">
                    <input
                      type="range"
                      min={loanFactorApiResponse.minLoanAmount}
                      max={loanFactorApiResponse.maxLoanAmount}
                      step={1000}
                      value={loanAmount}
                      onChange={handleSliderChange}
                      className="business-slider-input"
                    />
                    <div 
                      className="business-slider-thumb"
                      style={{
                        left: `${((loanAmount - loanFactorApiResponse.minLoanAmount) / (loanFactorApiResponse.maxLoanAmount - loanFactorApiResponse.minLoanAmount)) * 100}%`
                      }}
                    >
                      <span 
                        key={animationKey}
                        className="business-slider-icon"
                      >
                        <img src="/images/coin.png" alt="Slider Icon" />
                      </span>
                    </div>
                  </div>
                  <div className="business-slider-labels">
                    <span className="business-slider-label">{formatCurrency(loanFactorApiResponse.minLoanAmount)}</span>
                    <span className="business-slider-label">{formatCurrency(loanFactorApiResponse.maxLoanAmount)}</span>
                  </div>
                </div>
                {/* Mobile Number Input */}
                <div className={`business-input-field-container ${mobileNumberError ? 'business-input-field-error' : ''}`}>
                    <img src="/images/National_Flag_of_India.png" alt="India" className="business-input-flag" />
                    <input
                      type="tel"
                      placeholder=""
                      value={mobileNumber}
                      onChange={handleMobileNumberChange}
                      className="business-input-field"
                      maxLength="10"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                    <div className="business-hero-placeholder">{t('business-expansion-hero-section-mobile-placeholder')}</div>
                  </div>
                      
                {mobileNumberError && (
                  <div id="business-frm-error" className="business-error-message">
                    {mobileNumberError}
                  </div>
                )}
                </div>

              
               
            
                

                {/* Consent Section */}
                <div className="business-consent-section">
                  <div className="business-consent-content">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={handleConsentChange}
                      className="business-consent-checkbox"
                    />
                    <span className="business-consent-text">
                      {t('business-expansion-hero-section-consent-text')}
                    </span>
                    </div>
                </div>

                {/* Start Application Button */}
                <motion.button 
                  className="business-start-application-btn"
                  onClick={handleStartApplication}
                  disabled={isProcessing || !isFormValid()}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  {isProcessing ? t('business-expansion-hero-section-processing') : t('business-expansion-hero-section-button')}
                </motion.button>
                {errorMessage && (
                  <div id="business-frm-error" className="business-error-message">
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* OTP Verification Modal */}
      <OtpVerificationModal
        show={showOtpModal}
        mobile={mobileNumber}
        productType="Business Loan"
        onClose={() => setShowOtpModal(false)}
        onVerified={handleOtpSuccess}
      />
            </section>
  );
});

export default BusinessExpansionHeroSection;